import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Rocket, User, MapPin, Compass, CheckCircle2, ShieldCheck, Loader2, ArrowLeft, ArrowRight, Heart, Sparkles, UploadCloud, Quote, ExternalLink, Copy, Check, X, Phone } from 'lucide-react';
import Swal from 'sweetalert2';
import { API_ENDPOINTS } from '../../config/api';
import AnimatedSection from '../../components/common/AnimatedSection';

const InstagramIcon = ({ className = "w-4 h-4" }: { className?: string }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect width="20" height="20" x="2" y="2" rx="5" ry="5"/>
        <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/>
        <line x1="17.5" x2="17.51" y1="6.5" y2="6.5"/>
    </svg>
);

interface FollowProofItem {
    file: File;
    preview: string;
}

const SobatJofisahForm: React.FC = () => {
    const navigate = useNavigate();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [currentStep, setCurrentStep] = useState(1);
    const [supportType, setSupportType] = useState<'infaq' | 'free'>('infaq');

    const [formData, setFormData] = useState({
        name: '',
        gender: '',
        phone: '',
        generation: '',
        activity: '',
        domicile: '',
        is_interested_leader: false,
        leader_interest_area: '',
        leader_reason: '',
        concerns: '',
        goals: '',
        requested_topics: '',
        speaker_recommendation: '',
        donation_amount: 'Rp10.000 — Dukungan Seikhlasnya',
        agreement: false
    });

    // Bukti Follow Instagram (Bisa multiple file hingga 3+ gambar)
    const [followProofFiles, setFollowProofFiles] = useState<FollowProofItem[]>([]);

    // Bukti Infaq / Sodakoh
    const [donationProofFile, setDonationProofFile] = useState<File | null>(null);
    const [donationProofPreview, setDonationProofPreview] = useState<string | null>(null);
    const [isCopied, setIsCopied] = useState(false);

    const isDonating = supportType === 'infaq';

    const copyRekening = (text: string) => {
        navigator.clipboard.writeText(text);
        setIsCopied(true);
        setTimeout(() => setIsCopied(false), 2000);
    };

    const handleFollowProofChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (!files || files.length === 0) return;

        const newItems: FollowProofItem[] = [];
        let hasInvalidType = false;
        let hasTooLarge = false;

        Array.from(files).forEach((file) => {
            if (!file.type.startsWith('image/')) {
                hasInvalidType = true;
                return;
            }
            if (file.size > 5 * 1024 * 1024) {
                hasTooLarge = true;
                return;
            }
            newItems.push({
                file,
                preview: URL.createObjectURL(file)
            });
        });

        if (hasInvalidType) {
            Swal.fire('Format Salah', 'Beberapa file dilewati karena bukan gambar (JPG, PNG, WEBP).', 'warning');
        }
        if (hasTooLarge) {
            Swal.fire('File Terlalu Besar', 'Beberapa gambar dilewati karena melebihi batas 5MB.', 'error');
        }

        if (newItems.length > 0) {
            setFollowProofFiles(prev => {
                const combined = [...prev, ...newItems];
                if (combined.length > 5) {
                    Swal.fire('Info', 'Maksimal 5 bukti screenshot yang dapat diunggah.', 'info');
                    combined.slice(5).forEach(item => URL.revokeObjectURL(item.preview));
                    return combined.slice(0, 5);
                }
                return combined;
            });
        }
        e.target.value = '';
    };

    const removeFollowProof = (indexToRemove: number) => {
        setFollowProofFiles(prev => {
            const target = prev[indexToRemove];
            if (target) {
                URL.revokeObjectURL(target.preview);
            }
            return prev.filter((_, idx) => idx !== indexToRemove);
        });
    };

    const handleDonationProofChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            if (!file.type.startsWith('image/')) {
                Swal.fire('Format Salah', 'Harap unggah file berupa gambar (JPG, PNG, WEBP)', 'warning');
                e.target.value = '';
                return;
            }
            if (file.size > 5 * 1024 * 1024) {
                Swal.fire('File Terlalu Besar', 'Maksimal ukuran gambar adalah 5MB', 'error');
                e.target.value = '';
                return;
            }
            setDonationProofFile(file);
            setDonationProofPreview(URL.createObjectURL(file));
        }
    };

    const removeDonationProof = () => {
        setDonationProofFile(null);
        if (donationProofPreview) {
            URL.revokeObjectURL(donationProofPreview);
            setDonationProofPreview(null);
        }
    };

    const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let val = e.target.value.replace(/\D/g, '');
        if (val.startsWith('0')) {
            val = '62' + val.slice(1);
        } else if (val.startsWith('8')) {
            val = '62' + val;
        }
        setFormData(prev => ({ ...prev, phone: val }));
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value, type } = e.target;
        if (type === 'checkbox') {
            const checked = (e.target as HTMLInputElement).checked;
            setFormData(prev => ({ ...prev, [name]: checked }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
    };

    const nextStep = () => {
        if (currentStep === 1) {
            if (!formData.name.trim() || !formData.gender || !formData.phone.trim()) {
                return Swal.fire('Form Belum Lengkap', 'Harap isi seluruh field bertanda bintang (*)', 'warning');
            }
            const cleanPhone = formData.phone.replace(/\D/g, '');
            if (!cleanPhone.startsWith('62')) {
                return Swal.fire({
                    title: 'Format Nomor Salah',
                    text: 'Nomor WhatsApp wajib diawali 62 (contoh: 6281234567890).',
                    icon: 'warning',
                    confirmButtonColor: '#1E7F8A'
                });
            }
            if (cleanPhone.length < 10 || cleanPhone.length > 16) {
                return Swal.fire({
                    title: 'Nomor WhatsApp Tidak Valid',
                    text: 'Nomor WhatsApp harus terdiri dari 10 - 16 digit.',
                    icon: 'warning',
                    confirmButtonColor: '#1E7F8A'
                });
            }
        } else if (currentStep === 2) {
            if (!formData.generation || !formData.domicile || !formData.activity) {
                return Swal.fire('Form Belum Lengkap', 'Harap isi seluruh field bertanda bintang (*)', 'warning');
            }
        } else if (currentStep === 3) {
            if (!formData.concerns || !formData.goals) {
                return Swal.fire('Form Belum Lengkap', 'Harap isi seluruh field bertanda bintang (*)', 'warning');
            }
        }
        setCurrentStep(prev => Math.min(prev + 1, 4));
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const prevStep = () => {
        setCurrentStep(prev => Math.max(prev - 1, 1));
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!formData.agreement) {
            return Swal.fire('Persetujuan Diperlukan', 'Kamu harus menyetujui peraturan komunitas dan kebijakan privasi untuk bergabung.', 'warning');
        }

        const cleanPhone = formData.phone.replace(/\D/g, '');
        if (!cleanPhone || !cleanPhone.startsWith('62') || cleanPhone.length < 10) {
            return Swal.fire({
                title: 'Nomor WhatsApp Tidak Valid',
                text: 'Nomor WhatsApp wajib diisi dengan format 62 (contoh: 6281234567890).',
                icon: 'warning',
                confirmButtonColor: '#1E7F8A'
            });
        }

        if (supportType === 'infaq') {
            if (!formData.donation_amount || formData.donation_amount.startsWith('Rp0')) {
                return Swal.fire({
                    title: 'Pilih Nominal Infaq',
                    text: 'Kamu memilih opsi Infaq. Silakan pilih salah satu nominal di atas, atau beralih ke opsi "Belum Berinfaq (Rp0)".',
                    icon: 'warning',
                    confirmButtonColor: '#1E7F8A'
                });
            }
            if (!donationProofFile) {
                return Swal.fire({
                    title: 'Bukti Transfer Diperlukan',
                    text: `Kamu memilih infaq ${formData.donation_amount}. Harap unggah bukti transfer infaq kamu, atau pilih opsi "Belum Berinfaq (Rp0)" jika belum ingin berinfaq saat ini.`,
                    icon: 'warning',
                    confirmButtonColor: '#1E7F8A'
                });
            }
        } else {
            if (followProofFiles.length === 0) {
                return Swal.fire({
                    title: 'Bukti Follow Wajib Diunggah',
                    text: 'Karena kamu memilih belum berinfaq (Rp0), harap follow ketiga akun Instagram kami dan unggah screenshot bukti follow untuk melanjutkan.',
                    icon: 'warning',
                    confirmButtonColor: '#1E7F8A'
                });
            }
        }

        setIsSubmitting(true);

        try {
            const submitData = new FormData();
            Object.entries(formData).forEach(([key, val]) => {
                submitData.append(key, String(val));
            });
            followProofFiles.forEach((item) => {
                submitData.append('follow_proof', item.file);
            });
            if (donationProofFile) {
                submitData.append('donation_proof', donationProofFile);
            }

            const res = await fetch(`${API_ENDPOINTS.jofisah}/register`, {
                method: 'POST',
                body: submitData
            });

            const result = await res.json();

            if (res.ok) {
                Swal.fire({
                    title: 'Welcome to the Journey! 🚀',
                    html: `
                        <div style="font-size: 14px; color: #4B5563; line-height: 1.6;">
                            <p>Data kamu aman bersama kami.</p>
                            <p style="margin-top: 8px;">Silakan klik tombol di bawah untuk bergabung ke grup WhatsApp resmi <strong>Sobat Jofisah</strong> 🚀</p>
                        </div>
                    `,
                    icon: 'success',
                    confirmButtonColor: '#1E7F8A',
                    confirmButtonText: 'Gabung Grup WhatsApp 🚀',
                    showCancelButton: true,
                    cancelButtonText: 'Kembali ke Jofisah',
                    cancelButtonColor: '#9CA3AF',
                    allowOutsideClick: false
                }).then((result) => {
                    if (result.isConfirmed) {
                        window.location.href = 'https://chat.whatsapp.com/C2rxOCAbdZn26lXWrKNldU';
                    } else {
                        navigate('/jofisah');
                    }
                });
            } else {
                Swal.fire('Oops...', result.message, 'warning');
            }
        } catch (error) {
            Swal.fire('Error', 'Gagal terhubung ke server.', 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    const steps = [
        { id: 1, title: 'Data Diri', icon: <User size={18} /> },
        { id: 2, title: 'Domisili & Aktivitas', icon: <MapPin size={18} /> },
        { id: 3, title: 'Insight & Growth', icon: <Compass size={18} /> },
        { id: 4, title: 'Dukungan', icon: <Heart size={18} /> }
    ];

    const progressPercentage = (currentStep / steps.length) * 100;

    return (
        <div className="bg-[#FAF6EE] min-h-screen pt-28 pb-20 font-sans selection:bg-teal-100 selection:text-teal-900">
            <div className="max-w-3xl mx-auto px-4 sm:px-6">

                <AnimatedSection isHero={true}>
                    <div className="text-center mb-10">
                        <div className="inline-flex items-center justify-center p-3 bg-white/80 backdrop-blur shadow-sm text-teal-600 mb-6 border border-teal-600/10 rounded-full">
                            <Rocket size={24} />
                        </div>
                        <h1 className="text-3xl md:text-5xl font-extrabold text-gray-900 mb-4 leading-tight">
                            Gabung Sobat <br />
                            <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-600 to-amber-500">
                                Jofisah 🚀
                            </span>
                        </h1>
                        <p className="text-gray-600 text-base md:text-lg font-medium max-w-xl mx-auto">
                            Tempat bertumbuh, memperbaiki diri, dan menemukan arah hidup bersama.
                        </p>
                    </div>

                    <div className="mb-10">
                        <div className="flex justify-between items-center relative mb-4">
                            {steps.map((step) => (
                                <div key={step.id} className="flex flex-col items-center z-10 flex-1">
                                    <div 
                                        className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all duration-350 ${
                                            currentStep >= step.id 
                                                ? 'bg-teal-600 border-teal-600 text-white shadow-md' 
                                                : 'bg-white border-gray-200 text-gray-400'
                                        }`}
                                    >
                                        {step.icon}
                                    </div>
                                    <span className={`text-[10px] sm:text-xs font-bold mt-2 text-center transition-colors ${
                                        currentStep >= step.id ? 'text-teal-600' : 'text-gray-400'
                                    }`}>
                                        {step.title}
                                    </span>
                                </div>
                            ))}
                            <div className="absolute top-5 left-0 right-0 h-0.5 bg-gray-200 -z-10" />
                            <div 
                                className="absolute top-5 left-0 h-0.5 bg-teal-600 -z-10 transition-all duration-350"
                                style={{ width: `${((currentStep - 1) / (steps.length - 1)) * 100}%` }}
                            />
                        </div>

                        <div className="w-full bg-gray-200 h-1.5 rounded-full overflow-hidden">
                            <div 
                                className="bg-gradient-to-r from-teal-600 to-amber-500 h-full transition-all duration-300"
                                style={{ width: `${progressPercentage}%` }}
                            />
                        </div>
                    </div>
                </AnimatedSection>

                <AnimatedSection className="bg-white/75 backdrop-blur p-6 md:p-10 border border-white/20 rounded-[2.5rem] shadow-xl shadow-blue-900/5">
                    <form onSubmit={handleSubmit} className="space-y-8">

                        {currentStep === 1 && (
                            <div className="space-y-6 animate-in fade-in duration-300">
                                <div className="flex items-center gap-2 mb-4 border-b border-gray-100 pb-2">
                                    <User className="text-teal-600" size={20} />
                                    <h2 className="text-xl font-bold text-gray-900">Data Diri</h2>
                                </div>
                                <div className="space-y-5">
                                    <div>
                                        <label className="block text-sm font-bold text-gray-700 mb-2">Nama Lengkap *</label>
                                        <input type="text" name="name" required value={formData.name} onChange={handleChange} className="w-full p-4 rounded-xl bg-gray-50/50 border border-gray-200 outline-none transition-all focus:border-amber-500 focus:ring-4 focus:ring-amber-500/20" placeholder="Tuliskan nama lengkapmu..." />
                                    </div>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                                        <div>
                                            <label className="block text-sm font-bold text-gray-700 mb-2">Jenis Kelamin *</label>
                                            <select name="gender" required value={formData.gender} onChange={handleChange} className="w-full p-4 rounded-xl bg-gray-50/50 border border-gray-200 outline-none transition-all font-medium text-gray-700 focus:border-amber-500 focus:ring-4 focus:ring-amber-500/20">
                                                <option value="">-- Pilih --</option>
                                                <option value="Laki-laki">Laki-laki</option>
                                                <option value="Perempuan">Perempuan</option>
                                            </select>
                                        </div>
                                        <div>
                                            <div className="flex items-center justify-between mb-2">
                                                <label className="block text-sm font-bold text-gray-700">No WhatsApp *</label>
                                                <span className="text-[10px] font-bold text-teal-700 bg-teal-50 px-2 py-0.5 rounded-md border border-teal-100">
                                                    Format: 62
                                                </span>
                                            </div>
                                            <div className="relative">
                                                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-gray-400">
                                                    <Phone size={18} />
                                                </div>
                                                <input 
                                                    type="tel" 
                                                    name="phone" 
                                                    required 
                                                    value={formData.phone} 
                                                    onChange={handlePhoneChange} 
                                                    className="w-full pl-11 pr-4 py-4 rounded-xl bg-gray-50/50 border border-gray-200 outline-none transition-all font-medium focus:border-amber-500 focus:ring-4 focus:ring-amber-500/20" 
                                                    placeholder="6281234567890" 
                                                />
                                            </div>
                                            <p className="text-[11px] text-gray-500 mt-1.5 font-medium">
                                                Wajib diawali <span className="font-bold text-teal-700">62</span> (contoh: 6281234567890)
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {currentStep === 2 && (
                            <div className="space-y-6 animate-in fade-in duration-300">
                                <div className="flex items-center gap-2 mb-4 border-b border-gray-100 pb-2">
                                    <MapPin className="text-teal-600" size={20} />
                                    <h2 className="text-xl font-bold text-gray-900">Profil & Aktivitas</h2>
                                </div>
                                <div className="space-y-5">
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                                        <div>
                                            <label className="block text-sm font-bold text-gray-700 mb-2">Tahun Lahir (Generasi) *</label>
                                            <select name="generation" required value={formData.generation} onChange={handleChange} className="w-full p-4 rounded-xl bg-gray-50/50 border border-gray-200 outline-none transition-all text-gray-700 focus:border-amber-500 focus:ring-4 focus:ring-amber-500/20">
                                                <option value="">-- Pilih Generasi --</option>
                                                <option value="Gen Z (1997-2012)">Gen Z (1997-2012)</option>
                                                <option value="Millennial (1981-1996)">Millennial (1981-1996)</option>
                                                <option value="Lainnya">Lainnya</option>
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-bold text-gray-700 mb-2">Domisili *</label>
                                            <input type="text" name="domicile" required value={formData.domicile} onChange={handleChange} className="w-full p-4 rounded-xl bg-gray-50/50 border border-gray-200 outline-none transition-all focus:border-amber-500 focus:ring-4 focus:ring-amber-500/20" placeholder="Contoh: Bandung, Garut..." />
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-bold text-gray-700 mb-3">Kesibukan Saat Ini *</label>
                                        <div className="flex flex-wrap gap-3">
                                            {['Kuliah', 'Bekerja', 'Freelance/Usaha', 'Lainnya'].map((act) => (
                                                <label key={act} className={`cursor-pointer border px-6 py-3 rounded-xl font-bold text-sm transition-all ${formData.activity === act ? 'bg-teal-600/10 border-teal-600 text-teal-600 shadow-sm' : 'bg-white border-gray-200 text-gray-500 hover:bg-gray-50'}`}>
                                                    <input type="radio" name="activity" value={act} required checked={formData.activity === act} onChange={handleChange} className="hidden" />
                                                    {act}
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {currentStep === 3 && (
                            <div className="space-y-6 animate-in fade-in duration-300">
                                <div className="flex items-center gap-2 mb-4 border-b border-gray-100 pb-2">
                                    <Compass className="text-teal-600" size={20} />
                                    <h2 className="text-xl font-bold text-gray-900">Insight & Growth</h2>
                                </div>
                                <div className="space-y-5">
                                    <div>
                                        <label className="block text-sm font-bold text-gray-700 mb-2">Apa keresahanmu saat ini? *</label>
                                        <p className="text-xs text-teal-600 mb-3 font-semibold bg-teal-600/5 p-2.5 rounded-lg">🤝 Cerita aja, kita di sini untuk saling menguatkan.</p>
                                        <textarea name="concerns" required rows={4} value={formData.concerns} onChange={handleChange} className="w-full p-4 rounded-xl bg-gray-50/50 border border-gray-200 outline-none transition-all resize-none leading-relaxed focus:border-amber-500 focus:ring-4 focus:ring-amber-500/20" placeholder="Contoh: Merasa tertinggal dari teman, bingung arah karir, atau sulit konsisten..."></textarea>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-bold text-gray-700 mb-2">Apa harapan dan tujuan kamu bergabung? *</label>
                                        <textarea name="goals" required rows={3} value={formData.goals} onChange={handleChange} className="w-full p-4 rounded-xl bg-gray-50/50 border border-gray-200 outline-none transition-all resize-none leading-relaxed focus:border-amber-500 focus:ring-4 focus:ring-amber-500/20" placeholder="Aku ingin bisa lebih produktif dan punya circle yang positif..."></textarea>
                                    </div>
                                </div>
                            </div>
                        )}

                        {currentStep === 4 && (
                            <div className="space-y-6 animate-in fade-in duration-300">
                                <div className="flex items-center gap-2 mb-4 border-b border-gray-100 pb-2">
                                    <Heart className="text-teal-600" size={20} />
                                    <h2 className="text-xl font-bold text-gray-900">Dukungan & Persetujuan</h2>
                                </div>
                                <div className="space-y-6">
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                                        <div>
                                            <label className="block text-sm font-bold text-gray-700 mb-2">Request Tema Kajian (Opsional)</label>
                                            <input type="text" name="requested_topics" value={formData.requested_topics} onChange={handleChange} className="w-full p-4 rounded-xl bg-gray-50/50 border border-gray-200 outline-none focus:border-amber-500 focus:ring-4 focus:ring-amber-500/20" placeholder="Tema kajian yang kamu inginkan..." />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-bold text-gray-700 mb-2">Rekomendasi Pemateri (Opsional)</label>
                                            <input type="text" name="speaker_recommendation" value={formData.speaker_recommendation} onChange={handleChange} className="w-full p-4 rounded-xl bg-gray-50/50 border border-gray-200 outline-none focus:border-amber-500 focus:ring-4 focus:ring-amber-500/20" placeholder="Nama / IG pemateri favorit..." />
                                        </div>
                                    </div>

                                    {/* 1. PILIHAN BENTUK DUKUNGAN (PRIORITAS INFAQ) */}
                                    <div className="bg-gradient-to-br from-emerald-50/70 via-white to-teal-50/50 p-6 md:p-8 rounded-3xl border border-teal-600/15 shadow-sm space-y-6">
                                        <div className="space-y-2">
                                            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-teal-600/10 text-teal-700 text-xs font-bold tracking-wide">
                                                <Sparkles size={14} /> Dukungan Keberlanjutan Khazanah
                                            </div>
                                            <p className="text-sm md:text-[15px] text-gray-700 leading-relaxed font-medium">
                                                Untuk mendukung keberlanjutan program Khazanah dan menghadirkan pemateri berkualitas, kamu dapat memilih bentuk dukungan di bawah ini. Dukungan infaq sangat kami prioritaskan, namun bersifat sukarela.
                                            </p>
                                        </div>

                                        {/* Dua Pilihan Skema: Infaq (Prioritas) vs Belum Berinfaq (Rp0) */}
                                        <div>
                                            <label className="block text-sm font-bold text-gray-800 mb-3">
                                                Pilih Skema Dukungan:
                                            </label>
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                                {/* OPSI 1: PRIORITAS INFAQ */}
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        setSupportType('infaq');
                                                        if (!formData.donation_amount || formData.donation_amount.startsWith('Rp0')) {
                                                            setFormData(prev => ({ ...prev, donation_amount: 'Rp10.000 — Dukungan Seikhlasnya' }));
                                                        }
                                                    }}
                                                    className={`p-5 rounded-2xl border-2 text-left transition-all duration-200 relative flex flex-col justify-between cursor-pointer ${
                                                        supportType === 'infaq'
                                                            ? 'border-teal-600 bg-teal-600/10 shadow-md ring-2 ring-teal-600/20'
                                                            : 'border-gray-200/80 bg-white hover:border-teal-400 hover:bg-teal-50/30'
                                                    }`}
                                                >
                                                    <div className="flex items-start justify-between gap-2 mb-2">
                                                        <div className="flex items-center gap-2.5">
                                                            <span className="text-2xl">🌟</span>
                                                            <div>
                                                                <span className="inline-block text-[10px] font-black uppercase tracking-wider text-teal-700 bg-teal-100/80 px-2 py-0.5 rounded mb-1">
                                                                    Prioritas Khazanah
                                                                </span>
                                                                <h4 className="font-extrabold text-base text-gray-900">
                                                                    Berpartisipasi Infaq
                                                                </h4>
                                                            </div>
                                                        </div>
                                                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 mt-1 transition-all ${
                                                            supportType === 'infaq' ? 'border-teal-600 bg-teal-600' : 'border-gray-300'
                                                        }`}>
                                                            {supportType === 'infaq' && <CheckCircle2 size={12} className="text-white" />}
                                                        </div>
                                                    </div>
                                                    <p className="text-xs text-gray-600 leading-relaxed font-medium mt-1">
                                                        Bantu pemateri & operasional dakwah. <strong className="text-teal-700">Follow akun Instagram menjadi Opsional.</strong>
                                                    </p>
                                                </button>

                                                {/* OPSI 2: BELUM BERINFAQ (RP0) */}
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        setSupportType('free');
                                                        setFormData(prev => ({ ...prev, donation_amount: 'Rp0 (Belum Berinfaq)' }));
                                                        removeDonationProof();
                                                    }}
                                                    className={`p-5 rounded-2xl border-2 text-left transition-all duration-200 relative flex flex-col justify-between cursor-pointer ${
                                                        supportType === 'free'
                                                            ? 'border-teal-600 bg-teal-600/10 shadow-md ring-2 ring-teal-600/20'
                                                            : 'border-gray-200/80 bg-white hover:border-teal-400 hover:bg-teal-50/30'
                                                    }`}
                                                >
                                                    <div className="flex items-start justify-between gap-2 mb-2">
                                                        <div className="flex items-center gap-2.5">
                                                            <span className="text-2xl">📱</span>
                                                            <div>
                                                                <span className="inline-block text-[10px] font-black uppercase tracking-wider text-amber-700 bg-amber-100/80 px-2 py-0.5 rounded mb-1">
                                                                    Opsi Pengganti
                                                                </span>
                                                                <h4 className="font-extrabold text-base text-gray-900">
                                                                    Belum Berinfaq (Rp0)
                                                                </h4>
                                                            </div>
                                                        </div>
                                                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 mt-1 transition-all ${
                                                            supportType === 'free' ? 'border-teal-600 bg-teal-600' : 'border-gray-300'
                                                        }`}>
                                                            {supportType === 'free' && <CheckCircle2 size={12} className="text-white" />}
                                                        </div>
                                                    </div>
                                                    <p className="text-xs text-gray-600 leading-relaxed font-medium mt-1">
                                                        Belum bisa berinfaq saat ini? Cukup dukung kami dengan <strong className="text-gray-900">Wajib Follow Akun Instagram</strong>.
                                                    </p>
                                                </button>
                                            </div>
                                        </div>

                                        {/* KONTEN JIKA MEMILIH INFAQ */}
                                        {supportType === 'infaq' && (
                                            <div className="space-y-5 animate-in fade-in duration-200 pt-2 border-t border-teal-600/10">
                                                <div>
                                                    <label className="block text-sm font-bold text-gray-800 mb-3">
                                                        Silakan pilih nominal infaq sesuai kemampuan dan keikhlasan:
                                                    </label>
                                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                                        {[
                                                            { value: 'Rp100.000 — Dukungan Utama', label: 'Rp100.000', desc: 'Dukungan Utama' },
                                                            { value: 'Rp50.000 — Dukungan Menengah', label: 'Rp50.000', desc: 'Dukungan Menengah' },
                                                            { value: 'Rp30.000 — Dukungan Ringan', label: 'Rp30.000', desc: 'Dukungan Ringan' },
                                                            { value: 'Rp10.000 — Dukungan Seikhlasnya', label: 'Rp10.000', desc: 'Dukungan Seikhlasnya' }
                                                        ].map((tier) => {
                                                            const isSelected = formData.donation_amount === tier.value;
                                                            return (
                                                                <button
                                                                    key={tier.value}
                                                                    type="button"
                                                                    onClick={() => {
                                                                        setSupportType('infaq');
                                                                        setFormData(prev => ({
                                                                            ...prev,
                                                                            donation_amount: tier.value
                                                                        }));
                                                                    }}
                                                                    className={`text-left p-4 rounded-2xl border-2 transition-all duration-200 flex items-center justify-between group cursor-pointer ${
                                                                        isSelected
                                                                            ? 'border-teal-600 bg-teal-600/10 shadow-md ring-2 ring-teal-600/20'
                                                                            : 'border-gray-200/80 bg-white hover:border-teal-400 hover:bg-teal-50/30'
                                                                    }`}
                                                                >
                                                                    <div className="flex items-center gap-3">
                                                                        <span className="text-lg">🟢</span>
                                                                        <div>
                                                                            <p className="font-extrabold text-sm md:text-base text-gray-900 group-hover:text-teal-700 transition-colors">
                                                                                {tier.label}
                                                                            </p>
                                                                            <p className="text-xs text-gray-500 font-semibold">{tier.desc}</p>
                                                                        </div>
                                                                    </div>
                                                                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${
                                                                        isSelected ? 'border-teal-600 bg-teal-600' : 'border-gray-300 group-hover:border-teal-400'
                                                                    }`}>
                                                                        {isSelected && <CheckCircle2 size={12} className="text-white" />}
                                                                    </div>
                                                                </button>
                                                            );
                                                        })}

                                                    </div>
                                                    <div className="mt-3 flex justify-between items-center text-xs text-gray-500">
                                                        <span>* Pilihan di atas bersifat sukarela.</span>
                                                        {formData.donation_amount && (
                                                            <button
                                                                type="button"
                                                                onClick={() => {
                                                                    setSupportType('free');
                                                                    setFormData(prev => ({ ...prev, donation_amount: 'Rp0 (Belum Berinfaq)' }));
                                                                    removeDonationProof();
                                                                }}
                                                                className="text-teal-700 font-bold hover:underline cursor-pointer"
                                                            >
                                                                Beralih ke Belum Berinfaq (Rp0)
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>

                                                {/* Rekening Info & Bukti Infaq Upload (Muncul jika memilih nominal infaq) */}
                                                {formData.donation_amount && (
                                                    <div className="pt-4 border-t border-teal-600/10 space-y-4 animate-in fade-in duration-200">
                                                        <div>
                                                            <label className="block text-sm font-bold text-gray-900 mb-1">
                                                                Upload Bukti Transfer Infaq (Wajib *)
                                                            </label>
                                                            <p className="text-xs text-gray-500">
                                                                Kamu memilih <span className="font-bold text-teal-700">{formData.donation_amount}</span>. Silakan transfer melalui rekening resmi BCA berikut dan upload bukti transfernya:
                                                            </p>
                                                        </div>

                                                        {/* Info Rekening Bank */}
                                                        <div className="bg-white p-4 rounded-2xl border border-teal-600/20 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                                                            <div>
                                                                <span className="text-[10px] font-bold text-teal-700 uppercase tracking-wider bg-teal-50 px-2 py-0.5 rounded">
                                                                    Bank Central Asia (BCA)
                                                                </span>
                                                                <p className="font-mono font-bold text-gray-900 text-lg mt-1 tracking-wider">2728012345</p>
                                                                <p className="text-xs text-gray-500 font-medium">a.n. Khazanah Al Wahda</p>
                                                            </div>
                                                            <button
                                                                type="button"
                                                                onClick={() => copyRekening('2728012345')}
                                                                className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl border border-teal-600/30 text-teal-700 text-xs font-bold hover:bg-teal-50 transition cursor-pointer"
                                                            >
                                                                {isCopied ? <Check size={14} className="text-emerald-600" /> : <Copy size={14} />}
                                                                {isCopied ? 'Tersalin!' : 'Salin No. Rekening'}
                                                            </button>
                                                        </div>

                                                        {/* Donation Proof Dropzone */}
                                                        {!donationProofPreview ? (
                                                            <label className="border-2 border-dashed border-teal-600/30 hover:border-teal-600 bg-white/80 hover:bg-teal-50/20 rounded-2xl p-6 flex flex-col items-center justify-center cursor-pointer transition-all duration-200 group">
                                                                <div className="w-12 h-12 rounded-full bg-teal-600/10 text-teal-600 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                                                                    <UploadCloud size={24} />
                                                                </div>
                                                                <span className="text-sm font-bold text-gray-700 group-hover:text-teal-700">
                                                                    Klik untuk upload bukti transfer infaq / sodakoh *
                                                                </span>
                                                                <span className="text-xs text-gray-400 mt-1">
                                                                    Format JPG, PNG, WEBP (Maksimal 5MB)
                                                                </span>
                                                                <input
                                                                    type="file"
                                                                    accept="image/*"
                                                                    onChange={handleDonationProofChange}
                                                                    className="hidden"
                                                                />
                                                            </label>
                                                        ) : (
                                                            <div className="bg-white rounded-2xl p-4 border border-teal-600/20 shadow-sm flex flex-col sm:flex-row items-center gap-4">
                                                                <img
                                                                    src={donationProofPreview}
                                                                    alt="Bukti Infaq"
                                                                    className="w-20 h-20 object-cover rounded-xl border border-gray-200 shadow-inner"
                                                                />
                                                                <div className="flex-1 text-center sm:text-left min-w-0">
                                                                    <p className="text-sm font-bold text-gray-900 truncate">
                                                                        {donationProofFile?.name}
                                                                    </p>
                                                                    <p className="text-xs text-teal-700 font-semibold mt-0.5">
                                                                        {((donationProofFile?.size || 0) / 1024).toFixed(1)} KB • Bukti Infaq Terunggah ✅
                                                                    </p>
                                                                </div>
                                                                <button
                                                                    type="button"
                                                                    onClick={removeDonationProof}
                                                                    className="text-xs font-bold text-red-600 hover:text-red-700 bg-red-50 hover:bg-red-100 px-3.5 py-2 rounded-xl transition cursor-pointer"
                                                                >
                                                                    Hapus / Ganti
                                                                </button>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {/* KONTEN JIKA MEMILIH BELUM BERINFAQ (RP0) */}
                                        {supportType === 'free' && (
                                            <div className="bg-amber-50/80 border border-amber-200/80 rounded-2xl p-4 flex items-start gap-3 animate-in fade-in duration-200">
                                                <div className="w-8 h-8 rounded-full bg-amber-100 text-amber-800 flex items-center justify-center shrink-0 font-bold text-sm">
                                                    ℹ️
                                                </div>
                                                <div className="text-xs text-amber-900 space-y-1">
                                                    <p className="font-bold text-sm">Kamu memilih Belum Berinfaq (Rp0)</p>
                                                    <p className="leading-relaxed text-amber-800">
                                                        Tidak masalah! Sebagai gantinya, silakan dukung Khazanah dengan mem-follow 3 akun media sosial kami di bawah dan <strong>wajib mengunggah bukti screenshot follow</strong> untuk menyelesaikan pendaftaran.
                                                    </p>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {/* 2. BUKTI FOLLOW AKUN INSTAGRAM */}
                                    <div className={`bg-white p-6 md:p-8 rounded-3xl border transition-all duration-200 space-y-5 ${
                                        !isDonating 
                                            ? 'border-teal-600/40 shadow-md ring-2 ring-teal-600/15' 
                                            : 'border-gray-200/80 shadow-sm'
                                    }`}>
                                        <div className="flex items-start gap-3">
                                            <span className="text-xl">{!isDonating ? '📱' : '⚪'}</span>
                                            <div className="flex-1">
                                                <div className="flex flex-wrap items-center gap-2">
                                                    <h3 className="text-base font-extrabold text-gray-900">
                                                        Bukti Follow Instagram @masjid.atthohir @jofisah.id & @sholehahstory
                                                    </h3>
                                                    {isDonating ? (
                                                        <span className="inline-flex items-center gap-1 text-[11px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2.5 py-0.5 rounded-full">
                                                            <CheckCircle2 size={12} /> Opsional (Kamu sudah berinfaq)
                                                        </span>
                                                    ) : (
                                                        <span className="inline-flex items-center gap-1 text-[11px] font-bold text-amber-800 bg-amber-50 border border-amber-200 px-2.5 py-0.5 rounded-full">
                                                            ⚠️ Wajib Diisi * (Pengganti Infaq)
                                                        </span>
                                                    )}
                                                </div>
                                                <p className="text-xs md:text-sm text-gray-500 mt-1 leading-relaxed">
                                                    {isDonating ? (
                                                        <span>Alhamdulillah, jazakallah khair telah berinfaq! Bukti follow ini bersifat <strong>opsional (sukarela)</strong>. Kamu tetap dipersilakan follow agar selalu terupdate jadwal kajian.</span>
                                                    ) : (
                                                        <span>Karena kamu memilih belum berinfaq (Rp0), <strong>wajib follow ketiga akun Instagram resmi kami</strong> di bawah ini dan unggah tangkapan layar (screenshot) bukti follow kamu sebagai syarat pendaftaran.</span>
                                                    )}
                                                </p>
                                            </div>
                                        </div>

                                        {/* Instagram quick follow buttons */}
                                        <div className="flex flex-wrap gap-2.5 pt-1">
                                            <a
                                                href="https://instagram.com/masjid.atthohir"
                                                target="_blank"
                                                rel="noreferrer"
                                                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold bg-gray-50 border border-gray-200 text-gray-700 hover:border-pink-500 hover:text-pink-600 hover:bg-pink-50/30 transition shadow-2xs"
                                            >
                                                <InstagramIcon className="w-4 h-4 text-pink-600" />
                                                @masjid.atthohir
                                                <ExternalLink size={12} className="text-gray-400" />
                                            </a>
                                            <a
                                                href="https://instagram.com/jofisah.id"
                                                target="_blank"
                                                rel="noreferrer"
                                                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold bg-gray-50 border border-gray-200 text-gray-700 hover:border-teal-600 hover:text-teal-700 hover:bg-teal-50/30 transition shadow-2xs"
                                            >
                                                <InstagramIcon className="w-4 h-4 text-teal-600" />
                                                @jofisah.id
                                                <ExternalLink size={12} className="text-gray-400" />
                                            </a>
                                            <a
                                                href="https://instagram.com/sholehahstory"
                                                target="_blank"
                                                rel="noreferrer"
                                                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold bg-gray-50 border border-gray-200 text-gray-700 hover:border-rose-500 hover:text-rose-600 hover:bg-rose-50/30 transition shadow-2xs"
                                            >
                                                <InstagramIcon className="w-4 h-4 text-rose-500" />
                                                @sholehahstory
                                                <ExternalLink size={12} className="text-gray-400" />
                                            </a>
                                        </div>

                                        {/* Follow Proof Dropzone (Multi-upload untuk 3 Akun) */}
                                        <div className="space-y-3">
                                            {followProofFiles.length > 0 && (
                                                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                                                    {followProofFiles.map((item, idx) => (
                                                        <div 
                                                            key={idx} 
                                                            className="relative bg-white/90 rounded-2xl p-3 border border-teal-600/30 shadow-xs flex items-center gap-3 animate-in fade-in duration-200"
                                                        >
                                                            <img
                                                                src={item.preview}
                                                                alt={`Bukti follow ${idx + 1}`}
                                                                className="w-14 h-14 object-cover rounded-xl border border-gray-200 shadow-2xs shrink-0"
                                                            />
                                                            <div className="flex-1 min-w-0">
                                                                <p className="text-xs font-bold text-gray-900 truncate">
                                                                    {item.file.name}
                                                                </p>
                                                                <p className="text-[11px] text-teal-700 font-semibold mt-0.5">
                                                                    {((item.file.size || 0) / 1024).toFixed(1)} KB • Akun #{idx + 1} ✅
                                                                </p>
                                                            </div>
                                                            <button
                                                                type="button"
                                                                onClick={() => removeFollowProof(idx)}
                                                                className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition shrink-0 cursor-pointer"
                                                                title="Hapus bukti ini"
                                                            >
                                                                <X size={16} />
                                                            </button>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}

                                            {followProofFiles.length === 0 ? (
                                                <label className={`border-2 border-dashed rounded-2xl p-6 flex flex-col items-center justify-center cursor-pointer transition-all duration-200 group ${
                                                    !isDonating
                                                        ? 'border-teal-600/40 hover:border-teal-600 bg-teal-50/20 hover:bg-teal-50/40'
                                                        : 'border-gray-300 hover:border-teal-600 bg-gray-50/50 hover:bg-teal-50/20'
                                                }`}>
                                                    <div className="w-12 h-12 rounded-full bg-teal-600/10 text-teal-600 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                                                        <UploadCloud size={24} />
                                                    </div>
                                                    <span className="text-sm font-bold text-gray-700 group-hover:text-teal-700 text-center">
                                                        {!isDonating ? 'Klik untuk upload bukti screenshot follow 3 Akun (Wajib *)' : 'Klik untuk upload gambar bukti follow Instagram (Opsional)'}
                                                    </span>
                                                    <span className="text-xs text-gray-400 mt-1 text-center">
                                                        Format JPG, PNG, WEBP • Bisa pilih langsung lebih dari 1 file atau satu per satu (Maks. 5MB per file)
                                                    </span>
                                                    <input
                                                        type="file"
                                                        multiple
                                                        accept="image/*"
                                                        onChange={handleFollowProofChange}
                                                        className="hidden"
                                                    />
                                                </label>
                                            ) : (
                                                followProofFiles.length < 5 && (
                                                    <label className="border border-dashed border-teal-600/40 hover:border-teal-600 rounded-2xl p-3.5 flex items-center justify-center gap-2 cursor-pointer bg-teal-50/20 hover:bg-teal-50/40 text-xs font-bold text-teal-700 transition">
                                                        <UploadCloud size={16} />
                                                        <span>+ Tambah Screenshot Bukti Follow Akun Lainnya ({followProofFiles.length}/3)</span>
                                                        <input
                                                            type="file"
                                                            multiple
                                                            accept="image/*"
                                                            onChange={handleFollowProofChange}
                                                            className="hidden"
                                                        />
                                                    </label>
                                                )
                                            )}
                                        </div>
                                    </div>

                                    {/* 3. KUTIPAN PESAN */}
                                    <div className="bg-gradient-to-r from-amber-50/80 via-white to-amber-50/50 p-5 rounded-2xl border border-amber-200/70 shadow-xs flex items-center gap-3.5">
                                        <Quote className="text-amber-500 shrink-0" size={26} />
                                        <div>
                                            <p className="text-xs md:text-sm font-bold italic text-gray-800 leading-snug">
                                                "Sebaik-baik manusia adalah yang paling bermanfaat bagi manusia lain."
                                            </p>
                                            <p className="text-xs font-semibold text-amber-700 mt-1">
                                                Yuk sama-sama sebar manfaat.
                                            </p>
                                        </div>
                                    </div>

                                    <div className="bg-[#FAF6EE] p-5 rounded-2xl border border-gray-200 space-y-3.5">
                                        <div className="flex items-center gap-2 text-teal-600 font-bold">
                                            <ShieldCheck size={20} />
                                            <h3 className="font-outfit">Peraturan Komunitas & Kebijakan Privasi</h3>
                                        </div>
                                        <div className="text-xs text-gray-600 space-y-1 leading-relaxed pl-1 font-medium">
                                            <p>1. Wajib memperkenalkan diri setelah bergabung.</p>
                                            <p>2. Menjaga adab diskusi dan saling menghargai pendapat.</p>
                                            <p>3. Dilarang menyebarkan hoax, SARA, atau jualan tanpa izin.</p>
                                        </div>

                                        <div className="bg-white/90 p-3 rounded-xl border border-teal-200/70 text-xs text-gray-600 leading-relaxed flex items-start gap-2">
                                            <ShieldCheck size={16} className="text-teal-600 shrink-0 mt-0.5" />
                                            <span>
                                                Data pribadi dan curahan hatimu dijaga amanahnya sesuai prinsip syar'i dan UU PDP. Baca selengkapnya di{' '}
                                                <a 
                                                    href="/kebijakan-privasi" 
                                                    target="_blank" 
                                                    rel="noopener noreferrer" 
                                                    className="font-bold text-teal-700 underline hover:text-teal-900 inline-flex items-center gap-0.5"
                                                >
                                                    Kebijakan Privasi Khazanah <ExternalLink size={11} />
                                                </a>.
                                            </span>
                                        </div>

                                        <label className="flex items-start gap-3 cursor-pointer bg-white p-3.5 rounded-xl border border-gray-200 hover:border-teal-500 transition shadow-2xs">
                                            <input 
                                                type="checkbox" 
                                                name="agreement" 
                                                required 
                                                checked={formData.agreement} 
                                                onChange={handleChange} 
                                                className="mt-0.5 w-4.5 h-4.5 accent-teal-600 rounded cursor-pointer shrink-0" 
                                            />
                                            <span className="text-xs font-bold text-gray-800 leading-snug">
                                                Saya telah membaca dan berjanji untuk mengikuti semua aturan komunitas Sobat Jofisah serta menyetujui{' '}
                                                <a 
                                                    href="/kebijakan-privasi" 
                                                    target="_blank" 
                                                    rel="noopener noreferrer" 
                                                    onClick={(e) => e.stopPropagation()} 
                                                    className="text-teal-700 underline hover:text-teal-900 font-extrabold inline-flex items-center gap-0.5"
                                                >
                                                    Kebijakan Privasi <ExternalLink size={10} />
                                                </a>{' '}
                                                Khazanah demi kenyamanan dan ukhuwah bersama.
                                            </span>
                                        </label>
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="flex justify-between items-center pt-4 border-t border-gray-100">
                            {currentStep > 1 ? (
                                <button type="button" onClick={prevStep} className="flex items-center gap-1.5 px-5 py-3 rounded-xl border border-gray-200 hover:bg-gray-50 text-gray-600 font-bold text-sm transition">
                                    <ArrowLeft size={16} />
                                    <span>Sebelumnya</span>
                                </button>
                            ) : <div />}

                            {currentStep < 4 ? (
                                <button type="button" onClick={nextStep} className="flex items-center gap-1.5 px-6 py-3 rounded-xl bg-teal-600 hover:shadow-lg text-white font-bold text-sm transition ml-auto">
                                    <span>Lanjutkan</span>
                                    <ArrowRight size={16} />
                                </button>
                            ) : (
                                <button type="submit" disabled={isSubmitting || !formData.agreement} className="flex items-center gap-1.5 px-8 py-3 rounded-xl bg-gradient-to-r from-teal-600 to-amber-500 hover:shadow-lg text-white font-bold text-sm transition ml-auto disabled:opacity-50">
                                    {isSubmitting ? (
                                        <><Loader2 className="animate-spin" size={16} /> <span>Mendaftar...</span></>
                                    ) : (
                                        <><CheckCircle2 size={16} /> <span>Kirim Pendaftaran</span></>
                                    )}
                                </button>
                            )}
                        </div>
                    </form>
                </AnimatedSection>
            </div>
        </div>
    );
};

export default SobatJofisahForm;